/**
 * Telemetry event catalog — the typed contract for every marker emitted
 * across the app.
 *
 * Aligned with `docs/15_analytics_gdpr.md` §3 (event catalog). Each event
 * has:
 *   - `name`: a dotted lowercase string, stable forever. Renaming an
 *     event is a breaking change; add a new one instead.
 *   - `category`: groups events for dev-console filtering AND for
 *     consent gating (per category in `docs/18` settings — to be wired
 *     when the backend is live).
 *   - `payload`: strictly typed. Never includes raw PII; if a field
 *     would carry an answer or message, store a bucket/length instead.
 *
 * To add a new event:
 *   1. Add its `name` to `EventName` union.
 *   2. Add its payload shape to `EventPayloads`.
 *   3. Call `emit('your.event.name', { ...payload })` from the relevant
 *      code path. TypeScript will enforce the payload shape.
 */

export type EventCategory =
  | 'session'
  | 'game'
  | 'sound'
  | 'sensory'
  | 'onboarding'
  | 'payment'
  | 'profile'
  | 'consent'
  | 'perf'
  | 'error'
  | 'artifact'
  | 'upgrade'
  /** UX flow — JB 2026-05-26: «ux ui флоу аналитику надо тоже». */
  | 'ux'
  | 'funnel';

export type EventName =
  // ----- Session -----
  | 'session.start'
  | 'session.end'
  // ----- Game / core mechanic -----
  | 'tap.fired'
  | 'tap.streak.entered'
  | 'tap.streak.broken'
  | 'epoch.advanced'
  | 'crystallization.completed'
  | 'crystallization.skipped_zero'
  | 'phrase.shown'
  // ----- Sound -----
  | 'sound.played'
  | 'sound.skipped_muted'
  | 'sound.failed'
  // ----- Sensory random effects -----
  | 'sensory.effect.fired'
  | 'sensory.effect.skipped_cooldown'
  // ----- Onboarding -----
  | 'onboarding.shown'
  | 'onboarding.completed'
  | 'onboarding.abandoned'
  | 'soft_ask.shown'
  | 'soft_ask.completed'
  | 'soft_ask.skipped'
  // ----- Payment -----
  | 'paywall.shown'
  | 'payment.initiated'
  | 'payment.completed'
  | 'payment.failed'
  | 'payment.refunded'
  // ----- Profile -----
  | 'profile.question_shown'
  | 'profile.question_answered'
  | 'profile.question_skipped'
  | 'profile.sai_knows_you_shown'
  | 'profile.export_requested'
  | 'profile.wipe_requested'
  // ----- Consent -----
  | 'consent.given'
  | 'consent.withdrawn'
  // ----- Performance / errors -----
  | 'perf.fps_low'
  | 'perf.load_slow'
  | 'perf.fps_sample'
  | 'perf.slow_frame'
  | 'error.client'
  | 'error.boundary_caught'
  // ----- Milestone visual artifacts (doc 21) -----
  | 'artifact.unlocked'
  | 'artifact.reveal_shown'
  | 'artifact.reveal_dismissed'
  // ----- Upgrades (doc 14) -----
  | 'upgrade.purchased'
  | 'upgrade.purchase_denied'
  | 'upgrade.panel_opened'
  | 'upgrade.panel_closed'
  // ----- UX flow (JB 2026-05-26 «ux ui флоу аналитику») -----
  | 'screen.entered'
  | 'screen.exited'
  | 'panel.opened'
  | 'panel.closed'
  | 'activation.shown'
  | 'activation.dismissed'
  | 'return_greeting.shown'
  | 'return_greeting.dismissed'
  | 'visibility.changed'
  | 'idle.entered'
  // ----- Funnel firsts — каждый игрок проходит каждый milestone один раз -----
  | 'funnel.first';

/**
 * Payloads per event. Use a small, flat shape — easier to inspect in dev
 * console and easier to query later. Never put free-text answers here;
 * bucket or length-encode them at the call site.
 */
export interface EventPayloads {
  'session.start': { platform: 'web' | 'telegram' | 'android'; locale: string };
  'session.end': { duration_sec: number; taps_in_session: number };
  // Sampled — only every Nth tap fires this to keep volume sane.
  'tap.fired': { epoch: number; tap_total_bucket: number; layer: string };
  'tap.streak.entered': { length: number; epoch: number };
  'tap.streak.broken': { final_length: number; epoch: number };
  'epoch.advanced': { from_epoch: number; to_epoch: number; time_in_epoch_min: number };
  'crystallization.completed': { epoch: number; spirit_balls_gained: number };
  'crystallization.skipped_zero': { current_awarenesses: number };
  'phrase.shown': { phrase_id: string; layer: string; is_special: boolean };
  'sound.played': { event_id: string; variant_idx: number };
  'sound.skipped_muted': { event_id: string };
  'sound.failed': { event_id: string; reason: string };
  'sensory.effect.fired': { effect_id: string; class: string; epoch: number };
  'sensory.effect.skipped_cooldown': { effect_id: string; remaining_ms: number };
  'onboarding.shown': { funnel_step: string };
  'onboarding.completed': { duration_sec: number; skipped_steps: number };
  'onboarding.abandoned': { last_step: string; duration_sec: number };
  'soft_ask.shown': Record<string, never>;
  'soft_ask.completed': { has_name: boolean; has_dob: boolean };
  'soft_ask.skipped': Record<string, never>;
  'paywall.shown': { context: string; offer_id: string };
  'payment.initiated': { gateway: string; amount: number; currency: string };
  'payment.completed': { gateway: string; amount: number; currency: string; order_id: string };
  'payment.failed': { gateway: string; error_code: string };
  'payment.refunded': { order_id: string };
  'profile.question_shown': { question_id: string; trigger: string };
  'profile.question_answered': {
    question_id: string;
    answer_kind: 'choice' | 'text';
    length: number;
  };
  'profile.question_skipped': { question_id: string; reason: 'manual' | 'timeout' };
  'profile.sai_knows_you_shown': { answer_count: number; epoch: number };
  'profile.export_requested': Record<string, never>;
  'profile.wipe_requested': { had_account: boolean };
  'consent.given': { categories: readonly string[] };
  'consent.withdrawn': { categories: readonly string[] };
  'perf.fps_low': { avg_fps: number; browser: string; screen_size: string };
  'perf.load_slow': { load_time_ms: number; browser: string };
  /**
   * FPS sample emit'ится каждые ~10s через requestAnimationFrame loop.
   * Доп. метрики: p95 frame time + count медленных кадров (>50ms),
   * is_mobile + screen для cross-correlation с device class.
   */
  'perf.fps_sample': {
    avg_fps: number;
    p95_frame_ms: number;
    slow_frames: number;
    sample_seconds: number;
    is_mobile: boolean;
    screen_w: number;
    screen_h: number;
  };
  /** Один особо медленный кадр (>200ms = серьёзный stutter). Burst-prone, эмитим max раз/2s. */
  'perf.slow_frame': { frame_ms: number; since_load_s: number };
  'error.client': { error_msg: string; stack_trace_hash: string };
  'error.boundary_caught': { component: string; error_msg: string };
  'artifact.unlocked': { artifact_id: string; trigger_kind: string; epoch: number };
  'artifact.reveal_shown': { artifact_id: string; weight: 'major' | 'minor' };
  'artifact.reveal_dismissed': { artifact_id: string; duration_ms: number };
  'upgrade.purchased': {
    upgrade_id: string;
    new_level: number;
    cost_paid: number;
    currency: 'awarenesses' | 'spirit_balls';
  };
  'upgrade.purchase_denied': {
    upgrade_id: string;
    reason: 'locked' | 'insufficient_funds';
  };
  'upgrade.panel_opened': Record<string, never>;
  'upgrade.panel_closed': { duration_ms: number };
  // ----- UX flow -----
  /** Игрок вошёл на экран. source отделяет первичный mount от toggle'а. */
  'screen.entered': {
    screen: 'spark' | 'atlas';
    source: 'session_start' | 'toggle';
  };
  /** Игрок ушёл с экрана. duration_ms = сколько провёл на этом экране. */
  'screen.exited': { screen: 'spark' | 'atlas'; duration_ms: number };
  /** Любой floating panel/overlay (Settings, Debug). Upgrades имеет отдельные события. */
  'panel.opened': { panel: 'settings' | 'debug' };
  'panel.closed': { panel: 'settings' | 'debug'; duration_ms: number };
  /** Sai activation overlay показан. */
  'activation.shown': { activation_id: string; epoch: number };
  /** Sai activation dismissed. dismissed_by: 'button' (DelayedButton) | 'auto' (timeout). */
  'activation.dismissed': {
    activation_id: string;
    duration_ms: number;
    dismissed_by: 'button' | 'auto';
  };
  /**
   * Возврат после >12h — toast показан. hours_bucket: '12-24h' | '1-3d' | '3-7d' | '7d+'.
   * offline_bonus — пассивные осознания накопленные за время offline
   * (Mechanic D, capped at 8 hours). 0 если у игрока нет пассивных апгрейдов.
   */
  'return_greeting.shown': { hours_bucket: string; bonus: number; offline_bonus: number };
  'return_greeting.dismissed': { duration_ms: number };
  /** Page Visibility API — tab focused/blurred. */
  'visibility.changed': { state: 'visible' | 'hidden' };
  /** Игрок неактивен > IDLE_THRESHOLD_MS на текущем экране. */
  'idle.entered': { screen: 'spark' | 'atlas'; since_last_interaction_ms: number };
  // ----- Funnel firsts -----
  /** Игрок впервые достиг milestone — каждое значение firstId эмитится один раз за жизнь. */
  'funnel.first': {
    milestone:
      | 'first_tap'
      | 'first_phrase_shown'
      | 'first_crystallize_ready'
      | 'first_crystallize'
      | 'first_upgrade_purchased'
      | 'first_activation_seen'
      | 'first_lesson_unlocked'
      | 'first_atlas_visit'
      | 'first_settings_open'
      | 'first_epoch_advance';
    /** Время от старта сессии (или от mount funnel store) до milestone. */
    time_since_session_start_ms: number;
  };
}

/** Look up the category for any event name. */
export const EVENT_CATEGORY: Readonly<Record<EventName, EventCategory>> = {
  'session.start': 'session',
  'session.end': 'session',
  'tap.fired': 'game',
  'tap.streak.entered': 'game',
  'tap.streak.broken': 'game',
  'epoch.advanced': 'game',
  'crystallization.completed': 'game',
  'crystallization.skipped_zero': 'game',
  'phrase.shown': 'game',
  'sound.played': 'sound',
  'sound.skipped_muted': 'sound',
  'sound.failed': 'sound',
  'sensory.effect.fired': 'sensory',
  'sensory.effect.skipped_cooldown': 'sensory',
  'onboarding.shown': 'onboarding',
  'onboarding.completed': 'onboarding',
  'onboarding.abandoned': 'onboarding',
  'soft_ask.shown': 'onboarding',
  'soft_ask.completed': 'onboarding',
  'soft_ask.skipped': 'onboarding',
  'paywall.shown': 'payment',
  'payment.initiated': 'payment',
  'payment.completed': 'payment',
  'payment.failed': 'payment',
  'payment.refunded': 'payment',
  'profile.question_shown': 'profile',
  'profile.question_answered': 'profile',
  'profile.question_skipped': 'profile',
  'profile.sai_knows_you_shown': 'profile',
  'profile.export_requested': 'profile',
  'profile.wipe_requested': 'profile',
  'consent.given': 'consent',
  'consent.withdrawn': 'consent',
  'perf.fps_low': 'perf',
  'perf.load_slow': 'perf',
  'perf.fps_sample': 'perf',
  'perf.slow_frame': 'perf',
  'error.client': 'error',
  'error.boundary_caught': 'error',
  'artifact.unlocked': 'artifact',
  'artifact.reveal_shown': 'artifact',
  'artifact.reveal_dismissed': 'artifact',
  'upgrade.purchased': 'upgrade',
  'upgrade.purchase_denied': 'upgrade',
  'upgrade.panel_opened': 'upgrade',
  'upgrade.panel_closed': 'upgrade',
  'screen.entered': 'ux',
  'screen.exited': 'ux',
  'panel.opened': 'ux',
  'panel.closed': 'ux',
  'activation.shown': 'ux',
  'activation.dismissed': 'ux',
  'return_greeting.shown': 'ux',
  'return_greeting.dismissed': 'ux',
  'visibility.changed': 'ux',
  'idle.entered': 'ux',
  'funnel.first': 'funnel',
};
